import React, { useEffect, useRef } from 'react';

/* ── Types ──────────────────────────────────────────────────────────────── */

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'running';
  title: string;
  detail?: string;
}

interface ToastsProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

/* ── Constants ──────────────────────────────────────────────────────────── */

const MAX_VISIBLE = 4;
const AUTO_DISMISS_MS = 5000;

const borderColors: Record<Toast['type'], string> = {
  success: 'var(--status-completed)',
  error: 'var(--status-failed)',
  running: 'var(--status-running)',
};

const iconMap: Record<Toast['type'], string> = {
  success: '\u2713',
  error: '\u2717',
  running: '\u25CB',
};

/* ── Component ──────────────────────────────────────────────────────────── */

export default function Toasts({ toasts, onDismiss }: ToastsProps) {
  const visible = toasts.slice(-MAX_VISIBLE);

  return (
    <div
      role="region"
      aria-label="Notifications"
      // #285 QW6: the STABLE container is the live region (announces
      // inserted toasts reliably); per-toast aria-live on a freshly
      // mounted node is unreliable + can double-announce.
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 300,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      {visible.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

/* ── Single Toast ───────────────────────────────────────────────────────── */

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);

  // Auto-dismiss (not for running toasts)
  useEffect(() => {
    if (toast.type === 'running') return;
    const timer = setTimeout(() => onDismiss(toast.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [toast.id, toast.type, onDismiss]);

  // Enter animation
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateX(20px)';
    requestAnimationFrame(() => {
      el.style.transition = 'opacity 0.25s var(--ease-out), transform 0.25s var(--ease-out)';
      el.style.opacity = '1';
      el.style.transform = 'translateX(0)';
    });
  }, []);

  return (
    <div
      ref={ref}
      style={{
        width: 360,
        background: 'var(--bg-raised)',
        border: '1px solid var(--border-subtle)',
        borderLeft: `3px solid ${borderColors[toast.type]}`,
        borderRadius: 0,
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        pointerEvents: 'auto',
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
      }}
    >
      {/* Icon */}
      <span
        style={{
          color: borderColors[toast.type],
          fontSize: 14,
          fontWeight: 700,
          marginTop: 1,
          flexShrink: 0,
          animation: toast.type === 'running' ? 'spin 1.2s linear infinite' : undefined,
        }}
      >
        {iconMap[toast.type]}
      </span>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 2,
          }}
        >
          {toast.title}
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {toast.detail}
        </div>
      </div>

      {/* Dismiss */}
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label={`Dismiss: ${toast.title}`}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-tertiary)',
          fontSize: 14,
          cursor: 'pointer',
          padding: '0 2px',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        &times;
      </button>
    </div>
  );
}
